import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { getComplianceEmployees } from "../../../../api/hse/documentCompliance";
import {
  ComplianceDueDate,
  ComplianceKpi,
  ComplianceProgress,
} from "../../../../components/dashboard/hse/compliance/ComplianceBits";
import {
  EMPLOYEE_FILTERS,
  filterComplianceEmployees,
} from "../../../../components/dashboard/hse/compliance/complianceFilters";

/**
 * «Rutiner per ansatt» — every current employee's required reading at a glance:
 * progress, waiting, overdue, next deadline and routines without access.
 */
export const HseKsDocumentEmployees = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: false, data: null });
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    getComplianceEmployees()
      .then((data) => !cancelled && setState({ loading: false, error: false, data }))
      .catch(() => !cancelled && setState({ loading: false, error: true, data: null }));
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(
    () => filterComplianceEmployees(state.data?.employees || [], { query, filter }),
    [state.data, query, filter]
  );
  const summary = state.data?.summary || {};

  return (
    <HsePageChrome
      title={t("hseCompliance.employees.title")}
      subtitle={t("hseCompliance.employees.subtitle")}
      backTo="/dashboard/hse-ks/documents"
      actions={[
        {
          label: t("hseCompliance.employees.read_overview"),
          action: () => navigate("/dashboard/hse-ks/documents/read-status"),
          hidden: false,
          menu: false,
        },
      ]}
    >
      <div className="mx-auto mt-8 max-w-7xl px-4 pb-10 sm:px-6">
        {state.loading ? (
          <ResourceLoading />
        ) : state.error ? (
          <ResourceError backTo="/dashboard/hse-ks/documents" />
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <ComplianceKpi
                label={t("hseCompliance.employees.kpi_overdue")}
                value={summary.employees_with_overdue || 0}
                hint={t("hseCompliance.employees.kpi_overdue_hint", { count: summary.overdue_count || 0 })}
                tone={summary.employees_with_overdue ? "bad" : "ok"}
                pressed={filter === "overdue"}
                onClick={() => setFilter(filter === "overdue" ? "all" : "overdue")}
              />
              <ComplianceKpi
                label={t("hseCompliance.employees.kpi_pending")}
                value={summary.pending_count || 0}
                hint={t("hseCompliance.employees.kpi_pending_hint")}
                tone={summary.pending_count ? "warn" : undefined}
                pressed={filter === "pending"}
                onClick={() => setFilter(filter === "pending" ? "all" : "pending")}
              />
              <ComplianceKpi label={t("hseCompliance.employees.kpi_employees")} value={summary.employee_count || 0} />
              <ComplianceKpi label={t("hseCompliance.employees.kpi_documents")} value={summary.document_count || 0} />
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <label className="pm-picker-search min-w-0 flex-1 bg-[color:var(--pls-surface)]" style={{ height: 42, borderColor: "var(--pls-border)" }}>
                <MagnifyingGlassIcon className="h-4 w-4 text-[color:var(--pls-muted)]" aria-hidden="true" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t("hseCompliance.employees.search")}
                  aria-label={t("hseCompliance.employees.search")}
                />
              </label>
              <div className="pm-segmented pm-segmented--sm sm:w-[520px] sm:flex-none" role="radiogroup" aria-label={t("hseCompliance.employees.filter")}>
                {EMPLOYEE_FILTERS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={filter === value}
                    className={`pm-segmented__option${filter === value ? " is-active" : ""}`}
                    onClick={() => setFilter(value)}
                  >
                    {t(`hseCompliance.employees.filter_${value}`)}
                  </button>
                ))}
              </div>
            </div>

            {rows.length === 0 ? (
              <div className="pm-surface-card text-center">
                <div className="text-[15px] font-semibold text-[color:var(--pls-fg)]">{t("hseCompliance.employees.empty_title")}</div>
                <p className="mt-1 text-[13px] text-[color:var(--pls-muted)]">{t("hseCompliance.employees.empty_desc")}</p>
                {(query || filter !== "all") && (
                  <button type="button" className="pm-btn pm-btn--secondary pm-btn--sm mt-3" onClick={() => { setQuery(""); setFilter("all"); }}>
                    {t("hseCompliance.employees.clear_filters")}
                  </button>
                )}
              </div>
            ) : (
              <>
              <ul className="flex flex-col gap-2 sm:hidden">
                {rows.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      className="w-full rounded-[14px] border border-[color:var(--pls-border)] bg-[color:var(--pls-surface)] p-3 text-left"
                      onClick={() => navigate(`/dashboard/hse-ks/documents/employees/${row.id}`)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold">{row.name}</div>
                          <div className="text-[12px] text-[color:var(--pls-muted)]">{row.position || row.email}</div>
                        </div>
                        {row.overdue_count ? (
                          <span className="pm-badge pm-badge--bad">{t("hseCompliance.overview.overdue_count", { count: row.overdue_count })}</span>
                        ) : null}
                      </div>
                      <div className="mt-2">
                        <ComplianceProgress
                          value={row.acknowledged_count}
                          total={row.required_count}
                          label={t("hseCompliance.employees.progress_label", { done: row.acknowledged_count, total: row.required_count })}
                        />
                      </div>
                      <div className="mt-1 flex items-center justify-between text-[12.5px] text-[color:var(--pls-muted)]">
                        <span>{t("hseCompliance.status.pending")}: {row.pending_count}</span>
                        <ComplianceDueDate dueAt={row.next_due_at} />
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="hidden overflow-x-auto rounded-[14px] border border-[color:var(--pls-border)] bg-[color:var(--pls-surface)] sm:block">
                <table className="pm-table">
                  <thead>
                    <tr>
                      <th>{t("hseCompliance.employees.col_employee")}</th>
                      <th className="min-w-[180px]">{t("hseCompliance.employees.col_progress")}</th>
                      <th>{t("hseCompliance.status.pending")}</th>
                      <th>{t("hseCompliance.status.overdue")}</th>
                      <th>{t("hseCompliance.employees.col_next_due")}</th>
                      <th>{t("hseCompliance.employees.col_without_access")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.id}
                        className="cursor-pointer"
                        tabIndex={0}
                        onClick={() => navigate(`/dashboard/hse-ks/documents/employees/${row.id}`)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") navigate(`/dashboard/hse-ks/documents/employees/${row.id}`);
                        }}
                      >
                        <td>
                          <div className="font-semibold">{row.name}</div>
                          <div className="text-[12px] text-[color:var(--pls-muted)]">{row.position || row.email}</div>
                        </td>
                        <td>
                          <ComplianceProgress
                            value={row.acknowledged_count}
                            total={row.required_count}
                            label={t("hseCompliance.employees.progress_label", { done: row.acknowledged_count, total: row.required_count })}
                          />
                        </td>
                        <td className="tabular-nums">{row.pending_count}</td>
                        <td>
                          {row.overdue_count ? (
                            <span className="pm-badge pm-badge--bad">{row.overdue_count}</span>
                          ) : (
                            <span className="tabular-nums text-[color:var(--pls-faint)]">0</span>
                          )}
                        </td>
                        <td><ComplianceDueDate dueAt={row.next_due_at} /></td>
                        <td className="tabular-nums">
                          {row.without_access_count ? (
                            <span className="text-[color:var(--pls-muted)]">{row.without_access_count}</span>
                          ) : (
                            <span className="text-[color:var(--pls-faint)]">0</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>
        )}
      </div>
    </HsePageChrome>
  );
};

export default HseKsDocumentEmployees;
